/**
 * Enerji modeli — saf hesaplama. Ayrıntılı gerekçe ve kaynaklar: MOTOR.md
 *
 * Tasarımın özü: tek bir "aktivite çarpanı" yok. Taban (uyku + masa başı + TEF)
 * ile o gün gerçekten yapılan aktiviteler ayrı hesaplanıyor. Çarpan listesinin
 * ("hafif aktif / orta aktif") hakemli bir kaynağı yok ve bir kutucuk kayma
 * bu kullanıcıda 362 kcal/gün — panelin verebileceği tüm doğruluktan büyük.
 */

import type { Sex } from './body'

// ── Bazal metabolizma ───────────────────────────────────────────────────────

/**
 * Mifflin–St Jeor, orijinal katsayılarla. Academy of Nutrition and Dietetics'in
 * obez yetişkinler için resmî tavsiyesi ve test edilen denklemler içinde
 * ölçülen değerin ±%10'u içinde kalma oranı en yüksek olanı (obezlerde %70).
 *
 * Katch–McArdle (yağsız kütle tabanlı) bilerek kullanılmıyor: mezurayla
 * tahmin edilen yağ oranındaki hata tek başına ±87 kcal/gün belirsizlik
 * ekliyor ve kazancı yutuyor.
 */
export function bmr(weightKg: number, heightCm: number, age: number, sex: Sex): number {
  return 9.99 * weightKg + 6.25 * heightCm - 4.92 * age + (sex === 'male' ? 5 : -161)
}

/** BMR'nin bireysel hata payı ±%10 (1 SD ≈ 150–200 kcal). Tek sayı göstermiyoruz. */
export const BMR_UNCERTAINTY = 0.10

export function bmrRange(value: number): { low: number; high: number } {
  return { low: value * (1 - BMR_UNCERTAINTY), high: value * (1 + BMR_UNCERTAINTY) }
}

/**
 * Egzersiz dışı taban çarpanı: uyku + masa başı iş + yemeğin termik etkisi.
 * NASEM DRI 2023 "inactive" bandının (PAL < 1.53) alt ucu + %10 TEF.
 * Egzersiz bunun içinde DEĞİL, ayrıca ekleniyor.
 */
export const BASE_PAL = 1.15

// ── Aktivite kataloğu ───────────────────────────────────────────────────────

export interface ActivityDef {
  key: string
  name: string
  met: number
  /**
   * Vücut ağırlığını taşıyor mu? Bu bayrak formülü değiştiriyor ve %25–30'luk
   * bir fark yaratıyor — bkz. `activityKcal`.
   */
  weightBearing: boolean
  group: string
}

/**
 * 2024 Adult Compendium of Physical Activities değerleri.
 * Tenis burada özel değil, listedeki bir satır.
 */
export const ACTIVITIES: ActivityDef[] = [
  // Raket
  { key: 'tennis_doubles', name: 'Tenis — çiftli', met: 6.0, weightBearing: true, group: 'Raket' },
  { key: 'tennis_singles', name: 'Tenis — tekli', met: 6.8, weightBearing: true, group: 'Raket' },
  { key: 'tennis_comp', name: 'Tenis — rekabetçi', met: 8.0, weightBearing: true, group: 'Raket' },
  { key: 'table_tennis', name: 'Masa tenisi', met: 4.0, weightBearing: true, group: 'Raket' },
  { key: 'badminton', name: 'Badminton', met: 5.5, weightBearing: true, group: 'Raket' },

  // Yürüyüş / koşu
  { key: 'walk_slow', name: 'Yürüyüş — rahat (4.5–5.5 km/sa)', met: 3.8, weightBearing: true, group: 'Yürüyüş' },
  { key: 'walk_brisk', name: 'Yürüyüş — tempolu (5.6–6.3 km/sa)', met: 4.8, weightBearing: true, group: 'Yürüyüş' },
  { key: 'walk_fast', name: 'Yürüyüş — hızlı (6.4–7.1 km/sa)', met: 5.5, weightBearing: true, group: 'Yürüyüş' },
  { key: 'hiking', name: 'Doğa yürüyüşü', met: 5.3, weightBearing: true, group: 'Yürüyüş' },
  { key: 'stairs', name: 'Merdiven çıkma', met: 6.8, weightBearing: true, group: 'Yürüyüş' },
  { key: 'jog', name: 'Hafif koşu (jogging)', met: 7.5, weightBearing: true, group: 'Koşu' },
  { key: 'run_8', name: 'Koşu — 8 km/sa', met: 8.5, weightBearing: true, group: 'Koşu' },
  { key: 'run_10', name: 'Koşu — 9.7 km/sa', met: 9.3, weightBearing: true, group: 'Koşu' },

  // Bisiklet / makine
  { key: 'bike_moderate', name: 'Bisiklet — 16–19 km/sa', met: 6.8, weightBearing: true, group: 'Bisiklet' },
  { key: 'bike_vigorous', name: 'Bisiklet — 19–22 km/sa', met: 8.0, weightBearing: true, group: 'Bisiklet' },
  { key: 'stationary_bike', name: 'Kondisyon bisikleti', met: 6.0, weightBearing: false, group: 'Bisiklet' },
  { key: 'spin', name: 'Spin dersi', met: 9.0, weightBearing: false, group: 'Bisiklet' },
  { key: 'rowing_moderate', name: 'Kürek makinesi — orta', met: 5.0, weightBearing: false, group: 'Makine' },
  { key: 'rowing_hard', name: 'Kürek makinesi — yoğun', met: 7.3, weightBearing: false, group: 'Makine' },
  { key: 'elliptical', name: 'Eliptik', met: 5.0, weightBearing: false, group: 'Makine' },

  // Takım / su
  { key: 'swim_easy', name: 'Yüzme — rekreasyonel', met: 5.8, weightBearing: false, group: 'Su' },
  { key: 'swim_moderate', name: 'Yüzme — orta tempo', met: 8.0, weightBearing: false, group: 'Su' },
  { key: 'football_casual', name: 'Futbol — gündelik', met: 7.0, weightBearing: true, group: 'Takım' },
  { key: 'football_comp', name: 'Futbol — rekabetçi', met: 9.5, weightBearing: true, group: 'Takım' },
  { key: 'basketball', name: 'Basketbol', met: 7.5, weightBearing: true, group: 'Takım' },
  { key: 'volleyball', name: 'Voleybol', met: 4.0, weightBearing: true, group: 'Takım' },

  // Kuvvet / kondisyon
  { key: 'strength', name: 'Kuvvet antrenmanı (8–15 tekrar)', met: 3.5, weightBearing: false, group: 'Kuvvet' },
  { key: 'bodyweight', name: 'Vücut ağırlığı direnç', met: 3.0, weightBearing: false, group: 'Kuvvet' },
  { key: 'bodyweight_hard', name: 'Vücut ağırlığı — yoğun', met: 6.5, weightBearing: false, group: 'Kuvvet' },
  { key: 'calisthenics', name: 'Kalistenik — yoğun', met: 7.5, weightBearing: true, group: 'Kuvvet' },
  { key: 'circuit', name: 'Circuit — vücut ağırlığı', met: 6.0, weightBearing: true, group: 'Kuvvet' },
  { key: 'hiit_moderate', name: 'HIIT — orta', met: 7.0, weightBearing: true, group: 'Kuvvet' },
  { key: 'hiit_hard', name: 'HIIT — yoğun', met: 11.0, weightBearing: true, group: 'Kuvvet' },
  { key: 'jump_rope', name: 'İp atlama', met: 11.0, weightBearing: true, group: 'Kuvvet' },
  { key: 'boxing_bag', name: 'Boks — kum torbası', met: 5.8, weightBearing: true, group: 'Kuvvet' },

  // Düşük yoğunluk
  { key: 'yoga', name: 'Yoga', met: 2.3, weightBearing: false, group: 'Esneme' },
  { key: 'pilates', name: 'Pilates — mat', met: 1.8, weightBearing: false, group: 'Esneme' },
  { key: 'stretching', name: 'Esneme', met: 2.3, weightBearing: false, group: 'Esneme' },
]

export const ACTIVITY_BY_KEY: Record<string, ActivityDef> =
  Object.fromEntries(ACTIVITIES.map((a) => [a.key, a]))

export interface ActivityEntry {
  date: string
  activityKey: string
  minutes: number
  /**
   * Saatten/banttan okunan ÖLÇÜLEN aktif kalori. Doluysa MET tahminini eziyor.
   *
   * Apple Watch'ta "Aktif Kalori" gir, "Toplam Kalori" değil: toplam, o saati
   * yatarak geçirseydin de yakacağın dinlenme metabolizmasını içeriyor ve
   * bizim net hesabımızla çift sayılır.
   */
  kcalOverride?: number | null
  note?: string | null
}

/**
 * Bir aktivitenin NET kalorisi — yani o saati oturarak geçirseydi harcayacağının
 * ÜSTÜNDE ne harcadığı. Brüt göstermek, dinlenme metabolizmasını "kazanç" gibi
 * sunar; bu kullanıcıda 1 saat tenis için 86 kcal fark eder.
 *
 * İki ayrı yol var ve bu ince ama önemli:
 *
 * - **Ağırlık taşıyan** (yürüyüş, tenis, koşu): standart MET formülü doğruya
 *   yakın. Standart 1 MET tanımı obezlerde dinlenmeyi %31 fazla varsayıyor
 *   AMA obez bireyler verilen hızda daha verimsiz hareket ettiği için gerçek
 *   maliyet de yüksek — iki hata birbirini götürüyor. (2024 Compendium yürüyüş
 *   MET'lerini bu yüzden zaten yukarı çekti: 3.8 → 4.3 → 4.8.)
 *
 * - **Ağırlık taşımayan** (kondisyon bisikleti, ağırlık antrenmanı, yoga):
 *   fazla yağ kütlesi hareket ettirilmiyor, toplam kütleyle ölçeklemek aşırı
 *   tahmin üretir. Kişinin gerçek dinlenme hızıyla ölçekleniyor.
 */
export function activityKcal(
  entry: ActivityEntry, weightKg: number, bmrKcal: number,
): number {
  // Ölçülen değer her zaman tahmini yener — MET tablosu popülasyon ortalaması,
  // saat ise senin nabzına bakıyor. Süre 0 olsa bile ölçüm geçerli sayılıyor.
  if (entry.kcalOverride != null && entry.kcalOverride >= 0) return entry.kcalOverride

  const def = ACTIVITY_BY_KEY[entry.activityKey]
  if (!def || entry.minutes <= 0) return 0

  const hours = entry.minutes / 60
  const restPerHour = bmrKcal / 24
  const gross = def.weightBearing
    ? def.met * weightKg * hours
    : def.met * restPerHour * hours

  return Math.max(0, gross - restPerHour * hours)
}

export function dayActivityKcal(
  entries: ActivityEntry[], weightKg: number, bmrKcal: number,
): number {
  return entries.reduce((sum, e) => sum + activityKcal(e, weightKg, bmrKcal), 0)
}

// ── Günlük harcama ──────────────────────────────────────────────────────────

/**
 * O günün toplam harcaması. Taban + o gün loglanan aktiviteler.
 *
 * ÖNEMLİ: Bu sayı hedef kaloriyi yükseltmez. Egzersizle yakılan kalorinin
 * tamamı TDEE'ye eklenmiyor — ölçülen kompanzasyon %28, yüksek yağ kütlesinde
 * %49 (Careau 2021, n=1754). Uygulama egzersiz kalorisini "yeme hakkı" olarak
 * geri vermiyor; bu değer sadece haftalık enerji tablosunda görünüyor.
 */
export function dailyExpenditure(
  bmrKcal: number, entries: ActivityEntry[], weightKg: number,
): number {
  return bmrKcal * BASE_PAL + dayActivityKcal(entries, weightKg, bmrKcal)
}

/** Haftalık ortalama harcama — hedef kalori bunun üstünden kuruluyor. */
export function weeklyAverageExpenditure(
  bmrKcal: number, entries: ActivityEntry[], weightKg: number, days = 7,
): number {
  const total = entries.reduce((s, e) => s + activityKcal(e, weightKg, bmrKcal), 0)
  return bmrKcal * BASE_PAL + total / Math.max(1, days)
}

// ── Doku enerji yoğunluğu ───────────────────────────────────────────────────

const C_FORBES = 2.005      // kg — Hall/NIH modeli
const RHO_FAT = 9440        // kcal/kg
const RHO_LEAN = 1820       // kcal/kg

/**
 * 1 kg vücut ağırlığı değişiminin enerji karşılığı.
 *
 * Sabit 7700 kullanmıyoruz: kaybın yağ/yağsız bölüşümü yağ kütlesine bağlı
 * (Forbes). Yağ kütlesi 45 kg olan birinde kaybın %81'i yağ → 8010 kcal/kg;
 * yağ kütlesi 15 kg olanda %59 → 6320. Bu kullanıcıda sabit 7700 kullanmak
 * ~%4 (≈120 kcal/gün) hata demek. Kişi zayıfladıkça bu değer DÜŞÜYOR,
 * o yüzden her hesapta yeniden hesaplanıyor.
 */
export function rhoEff(weightKg: number, bodyFatPct: number): number {
  const fat = Math.max(0, weightKg * (bodyFatPct / 100))
  const pLean = C_FORBES / (C_FORBES + fat)
  return 1 / ((1 - pLean) / RHO_FAT + pLean / RHO_LEAN)
}

// ── Hedef kalori ────────────────────────────────────────────────────────────

/**
 * Hedef türü. Panel önce sadece kilo VERMEYİ biliyordu ve bu bir varsayım
 * değil, kabuldü: bütün metinler, kapalı çevrim ve hız kontrolü açık üzerine
 * kuruluydu. Korumak ve kas almak da meşru hedefler ve matematiği farklı.
 */
export type Goal = 'lose' | 'maintain' | 'gain'

export const GOAL_LABEL: Record<Goal, string> = {
  lose: 'Kilo ver',
  maintain: 'Kiloyu koru',
  gain: 'Kas al',
}

/**
 * Hedefe göre önerilen haftalık hız seçenekleri.
 *
 * Kas alımında sayılar bilerek küçük: fazlalığın bir kısmı zorunlu olarak yağ
 * olarak gidiyor ve haftada %0.25–0.5 vücut ağırlığından hızlısı yağ oranını
 * gereksiz yükseltiyor. "Bulk" diye ayda 4 kg almak kas değil kilo almaktır.
 */
export const RATE_OPTIONS: Record<Goal, { value: number; label: string }[]> = {
  lose: [
    { value: 0.5, label: '0.5 kg — yavaş' },
    { value: 0.7, label: '0.7 kg — dengeli' },
    { value: 0.9, label: '0.9 kg — agresif' },
  ],
  maintain: [{ value: 0, label: 'Kilo sabit kalsın' }],
  gain: [
    { value: 0.2, label: '0.2 kg — temiz' },
    { value: 0.35, label: '0.35 kg — dengeli' },
    { value: 0.5, label: '0.5 kg — hızlı' },
  ],
}

export function goalOf(v: string | null | undefined): Goal {
  return v === 'maintain' || v === 'gain' ? v : 'lose'
}

/** Açık üst sınırı. 500 kcal üstü yağsız kütle kazanımını engelliyor; VKİ>35'te 750 makul. */
export const MAX_DEFICIT = 750

export interface CalorieTarget {
  expenditure: number
  deficit: number
  target: number
  floor: number
  /** Taban devreye girdi mi — girdiyse hedeflenen hız gerçekleşmez. */
  floored: boolean
}

/**
 * `adjust`: kapalı çevrimin biriktirdiği düzeltme. TABANDAN ÖNCE uygulanıyor —
 * sonra uygulansaydı düzeltme tabanı delerdi ve "taban 1740" yazarken hedef
 * 1590 görünürdü. Taban, üzerine bir şey eklenip çıkarılan bir öneri değil,
 * aşılmaması gereken bir sınır.
 */
/** Kas alımında günlük fazlalık üst sınırı — üstü ağırlıklı olarak yağ. */
export const MAX_SURPLUS = 500

export function calorieTarget(
  expenditure: number, bmrKcal: number, weeklyRateKg: number, rho: number, adjust = 0,
  goal: Goal = 'lose',
): CalorieTarget {
  // Üç ayrı kural: klinik erkek tabanı, harcamanın %60'ı, BMR'nin %85'i.
  // Taban SADECE açık verirken anlamlı; korumada ve kas alımında zaten
  // harcamanın altına inilmiyor.
  const floor = Math.max(1500, expenditure * 0.6, bmrKcal * 0.85)

  if (goal === 'maintain') {
    const raw = expenditure + adjust
    const target = Math.max(raw, floor)
    return {
      expenditure, deficit: expenditure - target,
      target: Math.round(target / 10) * 10,
      floor: Math.round(floor / 10) * 10,
      floored: raw < floor,
    }
  }

  if (goal === 'gain') {
    const wanted = Math.min((weeklyRateKg * rho) / 7, MAX_SURPLUS)
    const target = expenditure + wanted + adjust
    return {
      expenditure, deficit: expenditure - target,   // negatif = fazlalık
      target: Math.round(target / 10) * 10,
      floor: Math.round(floor / 10) * 10,
      floored: false,
    }
  }

  const wanted = Math.min((weeklyRateKg * rho) / 7, MAX_DEFICIT)
  const raw = expenditure - wanted + adjust
  const target = Math.max(raw, floor)

  return {
    expenditure,
    deficit: expenditure - target,
    target: Math.round(target / 10) * 10,
    floor: Math.round(floor / 10) * 10,
    floored: raw < floor,
  }
}

/**
 * Protein hedefi.
 *
 * Kritik: literatürdeki 1.6–2.2 g/kg değerleri ZAYIF, antrenmanlı popülasyonda
 * türetildi. Obez birinin gerçek kilosuyla çarpmak anlamsız sayı üretir
 * (115 × 2.0 = 230 g). Referans ağırlık (VKİ 27.5 kapağı) veya yağsız kütle
 * kullanılıyor.
 */
export function proteinTarget(
  weightKg: number, heightCm: number, leanKg: number,
): { low: number; target: number; high: number } {
  const h = heightCm / 100
  const ref = Math.min(weightKg, 27.5 * h * h)

  // Alt sınır iki kuralın büyüğü; üst sınır yağsız kütlenin 2.4 katı.
  // Hedef bandın alt ucu DEĞİL, içinde bir nokta (2.0 g/kg yağsız kütle):
  // hedefi alt sınıra eşitlemek "bant" göstermeyi anlamsızlaştırıyor ve
  // kullanıcıya zaten minimumda olduğunu düşündürüyor.
  const low = Math.max(1.6 * ref, 1.8 * leanKg)
  const high = Math.max(low, 2.4 * leanKg)
  const target = Math.min(Math.max(2.0 * leanKg, low), high)

  return {
    low: Math.round(low / 5) * 5,
    target: Math.round(target / 5) * 5,
    high: Math.round(high / 5) * 5,
  }
}

// ── Kapalı çevrim: gözlenen hız → hedef düzeltmesi ──────────────────────────

/** Haftada en fazla bu kadar kaydırılır — ani sıçrama güven kaybettirir. */
export const MAX_ADJUST_PER_WEEK = 150
/** Bu eşiğin altındaki sapma gürültüdür, dokunulmaz. */
export const RATE_TOLERANCE = 0.25

export interface RateCheck {
  observedRate: number | null      // kg/hafta (negatif = kayıp)
  targetRate: number               // kg/hafta (pozitif sayı olarak kayıp hedefi)
  gap: number                      // kg/hafta, + = beklenenden yavaş
  suggestion: number               // kcal/gün, negatif = hedefi düşür
  verdict: 'ontrack' | 'slow' | 'fast' | 'insufficient' | 'stuck' | 'floor'
  message: string
}

/**
 * Gözlenen kilo eğimini hedefle karşılaştırır ve hedef kaloriye düzeltme önerir.
 *
 * Neden gerekli: statik formül 12 haftada 300–700 kcal/gün fazla tahmin ediyor
 * (adaptif termogenez + NEAT düşüşü). Plato budur.
 *
 * Neden "TDEE'yi geri hesapladık" DEMİYORUZ: uygulama ne yediğini bilmiyor.
 * Tek denklem, iki bilinmeyen. Gözlenen yavaşlığın sebebi tahminin yüksek
 * olması da olabilir, porsiyonların hedefin üstünde olması da. Ayırt edemiyoruz
 * ve bunu kullanıcıya açıkça söylüyoruz.
 *
 * `consecutiveCuts`: üst üste kaç kez düşürüldüğü. 3'ten sonra duruyoruz —
 * sorun tahminde değilse hedefi kovalamak kullanıcıyı tabana doğru iter.
 *
 * `atFloor`: hedef zaten kalori tabanına dayanmışsa düşürecek yer yok.
 * O durumda "hedefi 150 kcal düşürüyorum" demek yalan olur.
 */
export function rateCheck(
  observedRate: number | null, targetRate: number, rho: number,
  consecutiveCuts = 0, atFloor = false, goal: Goal = 'lose',
): RateCheck {
  const base = { observedRate, targetRate }

  if (goal !== 'lose' && observedRate !== null) return rateCheckOther(observedRate, targetRate, rho, goal)

  if (observedRate === null) {
    return {
      ...base, gap: 0, suggestion: 0, verdict: 'insufficient',
      message: 'Henüz yeterli tartı kaydı yok. En az 14 günlük veri gerekiyor — '
        + 'daha kısa aralıkta ölçüm gürültüsü gerçek değişimden büyük.',
    }
  }

  // observedRate negatif (kayıp), targetRate pozitif büyüklük.
  const actualLoss = -observedRate
  const gap = targetRate - actualLoss

  if (Math.abs(gap) < RATE_TOLERANCE) {
    return {
      ...base, gap, suggestion: 0, verdict: 'ontrack',
      message: `Haftada ${actualLoss.toFixed(2)} kg — hedefin (${targetRate.toFixed(2)}) `
        + 'üstünde değil altında değil. Hedef kaloriye dokunmuyorum.',
    }
  }

  if (gap < 0) {
    // Hedeflenenden hızlı. Aşırı hızlı kayıp yağsız kütleyi tehdit eder.
    const suggestion = Math.min(MAX_ADJUST_PER_WEEK, Math.round((-gap * rho) / 7 / 10) * 10)
    return {
      ...base, gap, suggestion, verdict: 'fast',
      message: `Haftada ${actualLoss.toFixed(2)} kg veriyorsun, hedef ${targetRate.toFixed(2)}. `
        + `Bu hızda kaybın daha büyük kısmı kastan gelir. Hedefi ${suggestion} kcal yukarı çekiyorum.`,
    }
  }

  if (atFloor) {
    return {
      ...base, gap, suggestion: 0, verdict: 'floor',
      message: `Haftada ${actualLoss.toFixed(2)} kg gidiyorsun, hedef ${targetRate.toFixed(2)}. `
        + 'Ama kalori hedefi zaten güvenli tabanda — daha aşağı çekecek yer yok. '
        + 'Buradan sonrası yemekten değil hareketten geçiyor: haftaya bir yürüyüş '
        + 'veya bir antrenman eklemek, aynı açığı yemeği kısmadan açar.',
    }
  }

  if (consecutiveCuts >= 3) {
    return {
      ...base, gap, suggestion: 0, verdict: 'stuck',
      message: 'Hedefi üç kez düşürdüm ve hız değişmedi. Bu noktada sorun tahminde '
        + 'değil: hedefi daha da düşürmek işe yaramaz, porsiyonları gözden geçirmek gerekiyor. '
        + 'Kalori hedefini olduğu yerde bırakıyorum.',
    }
  }

  const suggestion = -Math.min(MAX_ADJUST_PER_WEEK, Math.round((gap * rho) / 7 / 10) * 10)
  return {
    ...base, gap, suggestion, verdict: 'slow',
    message: `Son iki haftada haftada ${actualLoss.toFixed(2)} kg verdin, hedef ${targetRate.toFixed(2)}'di. `
      + 'Ya tahminimiz yüksek ya da porsiyonlar hedefin üstünde — uygulama ne yediğini '
      + `bilmediği için ayırt edemiyor. Hedefi ${Math.abs(suggestion)} kcal aşağı çekiyorum.`,
  }
}

/**
 * Kilo eğimi (kg/hafta), en küçük kareler. Ham tartı gürültüsü ±0.5–1.5 kg
 * olduğu için tek nokta farkı değil eğim kullanılıyor.
 *
 * `null` dönerse veri yetersiz: 14 günden az ölçümde eğimin standart hatası
 * ±370 kcal/gün üstünde, yani formülden bile kötü.
 */
export function weightSlope(
  points: { date: string; kg: number }[], minDays = 14,
): number | null {
  if (points.length < 4) return null

  const t0 = Date.parse(points[0].date + 'T00:00:00')
  const xs = points.map((p) => (Date.parse(p.date + 'T00:00:00') - t0) / 86_400_000)
  const span = xs[xs.length - 1] - xs[0]
  if (span < minDays) return null

  const n = xs.length
  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = points.reduce((a, b) => a + b.kg, 0) / n

  let num = 0
  let den = 0
  for (let i = 0; i < n; i++) {
    num += (xs[i] - mx) * (points[i].kg - my)
    den += (xs[i] - mx) ** 2
  }
  if (den === 0) return null

  return (num / den) * 7      // kg/gün → kg/hafta
}

/**
 * Koruma ve kas alma hedeflerinde hız kontrolü.
 *
 * Kilo vermeden farklı olan şey işaret değil, **neyin kötü olduğu**:
 *  - Korumada her iki yön de sapma; ±0.25 kg/hafta bandı içinde kalmak yeterli.
 *  - Kas alımında hızlı artış "iyi gidiyor" değil, fazlalığın yağa gittiği
 *    anlamına geliyor. Hedefin üstü de aşağı çekiliyor.
 *
 * Kilo kaybındaki "taban" mantığı burada yok: korumada ve fazlada kalori
 * hedefi zaten harcamanın altına inmiyor.
 */
function rateCheckOther(
  observed: number, targetRate: number, rho: number, goal: Goal,
): RateCheck {
  const base = { observedRate: observed, targetRate }
  const kcal = (kgPerWeek: number) =>
    Math.min(MAX_ADJUST_PER_WEEK, Math.round((Math.abs(kgPerWeek) * rho) / 7 / 10) * 10)
  const w = (v: number) => `${Math.abs(v).toFixed(2)} kg`

  if (goal === 'maintain') {
    if (Math.abs(observed) < RATE_TOLERANCE) {
      return {
        ...base, gap: 0, suggestion: 0, verdict: 'ontrack',
        message: `Haftada ${w(observed)} — koruma bandının (±0.25) içindesin. `
          + 'Hedef kaloriye dokunmuyorum; bu bantta salınmak normal, günlük '
          + 'dalgalanma zaten 1–2 kg olabiliyor.',
      }
    }
    const suggestion = observed > 0 ? -kcal(observed) : kcal(observed)
    return {
      ...base, gap: -observed, suggestion,
      verdict: observed > 0 ? 'fast' : 'slow',
      message: observed > 0
        ? `Haftada ${w(observed)} alıyorsun ama hedefin korumak. Hedefi ${suggestion} kcal `
          + 'ayarlıyorum. Kas alma niyetin varsa hedefi değiştirmek daha doğru.'
        : `Haftada ${w(observed)} veriyorsun ama hedefin korumak. Hedefi +${Math.abs(suggestion)} kcal `
          + 'çekiyorum — istemeden açık veriyorsun ve bu dönemde kas kaybı riski var.',
    }
  }

  // gain
  const gap = targetRate - observed          // + ise yavaş, − ise hızlı
  if (Math.abs(gap) < RATE_TOLERANCE) {
    return {
      ...base, gap, suggestion: 0, verdict: 'ontrack',
      message: `Haftada ${w(observed)} alıyorsun, hedef ${targetRate.toFixed(2)}. `
        + 'Tam bandında; hedef kaloriye dokunmuyorum.',
    }
  }
  if (gap < 0) {
    const suggestion = -kcal(gap)
    return {
      ...base, gap, suggestion, verdict: 'fast',
      message: `Haftada ${w(observed)} alıyorsun, hedef ${targetRate.toFixed(2)}. `
        + `Bu hızda fazlalığın büyük kısmı yağ olarak gidiyor — hedefi ${suggestion} kcal `
        + 'aşağı çekiyorum. Kas kazanımının bir hız sınırı var ve daha çok yemek onu aşmıyor.',
    }
  }
  const suggestion = kcal(gap)
  return {
    ...base, gap, suggestion, verdict: 'slow',
    message: `Haftada ${w(observed)} alıyorsun, hedef ${targetRate.toFixed(2)}. `
      + `Hedefi +${suggestion} kcal çıkarıyorum. Tartı iki hafta üst üste yerinde `
      + 'duruyorsa fazlalık gerçekte yok demektir.',
  }
}
